import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Building2, ImagePlus, Plus, Trash2 } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao } from '../../componentes/base';
import { Chatter } from '../../componentes/chatter';
import { Estado, Vazio } from '../../componentes/estados';
import { mascaraCep, prontoParaConsulta, type DefCampo } from '../../componentes/formulario';
import { ConfirmarExclusao } from '../../componentes/confirmar-exclusao';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { useSessao, veTodasAsEmpresas, type DadosSessao } from '../../nucleo/sessao';
import { CamposForm, GRADE_FORM, useEstadoForm } from './form';
import { formatarDocumentoPais, mascararCodigoPostal, moedaDoPais } from '@malgoos/shared';
import { cnpjFormatado, type Empresa } from './tipos';
import { documentoDoPais } from '../../nucleo/formato';
import { nomeProvinciaEs, opcoesEstadoEua, opcoesMoedaEmpresa, opcoesPais, opcoesProvinciaEs } from '../../nucleo/paises';
import { TextoCortado } from '../../componentes/texto-cortado';

const LIMITE_LOGO = 2 * 1024 * 1024;

const urlLogo = (id: string) => `/api/v1/anexos/${id}`;

/** Bloco de logo: o arquivo vai pela rota de anexos do núcleo com a entidade `configuracoes.empresa`. */
function BlocoLogo({ empresa, aoMudar }: { empresa: Empresa; aoMudar: () => Promise<unknown> }) {
  const { t } = useTranslation('configuracoes');
  const s = useSessao();
  const toast = useToast();
  const entrada = useRef<HTMLInputElement>(null);
  const enviar = useMutation({
    mutationFn: async (f: File) => {
      const fd = new FormData();
      fd.append('entidade', 'configuracoes.empresa');
      fd.append('registro_id', empresa.id);
      fd.append('arquivo', f);
      const anexo = await api<{ id: string }>('POST', '/anexos', fd);
      await patch(`/configuracoes/empresas/${empresa.id}`, { logo_anexo_id: anexo.id });
    },
    onSuccess: aoMudar,
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const remover = useMutation({ mutationFn: () => patch(`/configuracoes/empresas/${empresa.id}`, { logo_anexo_id: null }), onSuccess: aoMudar, onError: (e) => toast.erro(mensagemDeErro(e)) });

  function escolher(f: File | undefined) {
    if (!f) return;
    if (!f.type.startsWith('image/')) return toast.erro(t('empresa.logoTipo'));
    if (f.size > LIMITE_LOGO) return toast.erro(t('empresa.logoGrande'));
    enviar.mutate(f);
  }

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-4 rounded-xl border border-borda bg-sup-2 p-3 sm:col-span-6">
      <div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-borda bg-sup-1 text-tinta-3">
        {empresa.logo_anexo_id ? <img src={urlLogo(empresa.logo_anexo_id)} alt={t('empresa.logo')} className="size-full object-contain" /> : <Building2 className="size-6" aria-hidden />}
      </div>
      <div className="flex min-w-0 flex-1 basis-48 flex-col gap-1">
        <p className="text-sm font-medium text-tinta-2">{t('empresa.logo')}</p>
        <p className="text-xs text-tinta-3">{empresa.logo_anexo_id ? t('empresa.logoDica') : `${t('empresa.semLogo')}. ${t('empresa.logoDica')}`}</p>
      </div>
      {s.pode('configuracoes.empresas') && (
        <div className="flex flex-wrap gap-2">
          <input ref={entrada} type="file" accept="image/*" className="sr-only" tabIndex={-1} aria-label={t('empresa.enviarLogo')} title={t('empresa.enviarLogo')} onChange={(e) => escolher(e.target.files?.[0])} />
          <Botao tamanho="sm" icone={<ImagePlus className="size-4" />} carregando={enviar.isPending} onClick={() => entrada.current?.click()}>
            {empresa.logo_anexo_id ? t('empresa.trocarLogo') : t('empresa.enviarLogo')}
          </Botao>
          {empresa.logo_anexo_id && (
            <Botao tamanho="sm" variante="fantasma" carregando={remover.isPending} onClick={() => remover.mutate()}>
              {t('empresa.removerLogo')}
            </Botao>
          )}
        </div>
      )}
    </div>
  );
}

type EstadoConsulta = 'buscando' | 'preenchido' | 'naoEncontrado' | 'falha';
interface RespostaConsulta {
  situacao: 'encontrado' | 'nao_encontrado' | 'falha' | 'limite' | null;
  dados: Record<string, unknown> | null;
}

/** Endereço da consulta nos nomes deste formulário (sem o CEP, que é o que se digitou ou o do CNPJ). */
function enderecoDaConsulta(d: Record<string, unknown> | null | undefined, comCep: boolean): Record<string, unknown> {
  if (!d) return {};
  const s = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
  return {
    ...(comCep && s(d.cep) ? { cep: mascaraCep(String(d.cep)) } : {}),
    logradouro: s(d.logradouro),
    numero: s(d.numero),
    complemento: s(d.complemento),
    bairro: s(d.bairro),
    cidade: s(d.cidade),
    uf: s(d.uf),
  };
}

/** Lote 19/10: país da empresa no formulário (padrão BR) e se ele tem documento com tipo próprio (BR, EUA, Espanha). */
const paisDe = (v: Record<string, unknown>) => String(v.pais || 'BR');
const noPais = (p: string) => (v: Record<string, unknown>) => paisDe(v) === p;
const outroPais = (v: Record<string, unknown>) => !['BR', 'US', 'ES'].includes(paisDe(v));
/** Tipo do documento da empresa pelo país: CNPJ, EIN, NIF de empresa (CIF) ou o digitado nos outros. */
function tipoDoDocumento(pais: string, digitado: unknown, documento: unknown): string | null {
  if (pais === 'BR') return documento ? 'cnpj' : null;
  if (pais === 'US') return 'ein';
  if (pais === 'ES') return 'cif';
  return String(digitado ?? '').trim() || null;
}
/** Documento gravado como aparece no campo: CNPJ e EIN com a máscara; Espanha e outros países como gravado. */
function documentoNoCampo(e: Empresa | null): string {
  if (!e?.documento) return '';
  const pais = e.pais || 'BR';
  if (pais === 'BR') return cnpjFormatado(e.documento);
  return pais === 'US' || pais === 'ES' ? formatarDocumentoPais(e.documento_tipo ?? (pais === 'US' ? 'ein' : 'cif'), e.documento) : e.documento;
}

export function FormEmpresa({ empresa, aoSalvar }: { empresa: Empresa | null; aoSalvar: (id: string) => Promise<unknown> }) {
  const { t } = useTranslation('configuracoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const e = empresa?.endereco ?? {};
  // Lote 19/10 (item 22): a mesma consulta de CNPJ e CEP do cadastro de Pessoas e empresas (conector BrasilAPI pelo servidor),
  // só para quem pode consultar; sem a permissão ou com a consulta fora, a máscara segue e o preenchimento é à mão.
  const [consulta, setConsulta] = useState<{ documento?: EstadoConsulta; cep?: EstadoConsulta }>({});
  const ultima = useRef<Record<string, string>>({});
  const f = useEstadoForm<Record<string, unknown>>({
    razao_social: empresa?.razao_social ?? '',
    nome_fantasia: empresa?.nome_fantasia ?? '',
    pais: empresa?.pais ?? 'BR',
    moeda: empresa?.moeda ?? 'BRL',
    documento_tipo: empresa && !['BR', 'US', 'ES'].includes(empresa.pais ?? 'BR') ? (empresa.documento_tipo ?? '') : '',
    documento: documentoNoCampo(empresa),
    cep: e.cep ?? '',
    logradouro: e.logradouro ?? '',
    numero: e.numero ?? '',
    complemento: e.complemento ?? '',
    bairro: e.bairro ?? '',
    cidade: e.cidade ?? '',
    uf: e.uf ?? '',
  });
  const defs: DefCampo[] = [
    { nome: 'razao_social', rotulo: t('empresa.razao'), tipo: 'texto', obrigatorio: true },
    { nome: 'nome_fantasia', rotulo: t('empresa.fantasia'), tipo: 'texto', largura: 'meia' },
    // Lote 19/10: país e moeda; o documento, o código postal e a região seguem o país (máscara e consulta só no Brasil).
    { nome: 'pais', rotulo: t('empresa.pais'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: opcoesPais() },
    { nome: 'moeda', rotulo: t('empresa.moeda'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: opcoesMoedaEmpresa() },
    { nome: 'documento', rotulo: t('empresa.cnpj'), tipo: 'documento', documento: 'cnpj', largura: 'meia', placeholder: '00.000.000/0000-00', ajuda: consulta.documento && t(`empresa.consulta.${consulta.documento}`), visivel: noPais('BR') },
    { nome: 'documento', rotulo: tn('documento.tipos.ein'), tipo: 'documento', documento: 'ein', largura: 'meia', visivel: noPais('US') },
    { nome: 'documento', rotulo: tn('documento.tipos.cif'), tipo: 'documento', documento: 'cif', largura: 'meia', visivel: noPais('ES') },
    { nome: 'documento_tipo', rotulo: tn('documento.tipo'), tipo: 'texto', largura: 'meia', placeholder: 'VAT', visivel: outroPais },
    { nome: 'documento', rotulo: tn('documento.registro'), tipo: 'texto', largura: 'meia', sensivel: 'documento', visivel: outroPais },
    { nome: 'cep', rotulo: t('empresa.cep'), tipo: 'cep', largura: 'terco', ajuda: consulta.cep && t(`empresa.consulta.${consulta.cep}`), visivel: noPais('BR') },
    { nome: 'cep', rotulo: tn('endereco.zip'), tipo: 'texto', largura: 'terco', placeholder: '00000-0000', sensivel: 'oculto', visivel: noPais('US') },
    { nome: 'cep', rotulo: tn('endereco.codigoPostal'), tipo: 'texto', largura: 'terco', placeholder: '00000', sensivel: 'oculto', visivel: (v) => !noPais('BR')(v) && !noPais('US')(v) },
    { nome: 'logradouro', rotulo: t('empresa.logradouro'), tipo: 'texto', largura: 'meia' },
    { nome: 'numero', rotulo: t('empresa.numero'), tipo: 'texto', largura: 'terco', sensivel: 'oculto' },
    { nome: 'complemento', rotulo: t('empresa.complemento'), tipo: 'texto', largura: 'meia' },
    { nome: 'bairro', rotulo: t('empresa.bairro'), tipo: 'texto', largura: 'meia' },
    { nome: 'cidade', rotulo: t('empresa.cidade'), tipo: 'texto', largura: 'meia' },
    { nome: 'uf', rotulo: t('empresa.uf'), tipo: 'texto', largura: 'terco', visivel: noPais('BR') },
    { nome: 'uf', rotulo: tn('endereco.estado'), tipo: 'selecao', largura: 'terco', opcoes: opcoesEstadoEua(), visivel: noPais('US') },
    { nome: 'uf', rotulo: tn('endereco.provincia'), tipo: 'selecao', largura: 'terco', opcoes: opcoesProvinciaEs(), visivel: noPais('ES') },
    { nome: 'uf', rotulo: tn('endereco.estadoProvincia'), tipo: 'texto', largura: 'terco', visivel: outroPais },
  ];

  async function consultar(campo: 'documento' | 'cep', valor: string) {
    setConsulta((c) => ({ ...c, [campo]: 'buscando' }));
    let r: RespostaConsulta;
    try {
      r = await get<RespostaConsulta>(`/cadastros/consultas/${campo === 'documento' ? 'cnpj' : 'cep'}/${valor}`);
    } catch {
      r = { situacao: 'falha', dados: null };
    }
    if (r.situacao !== 'encontrado' || !r.dados) return setConsulta((c) => ({ ...c, [campo]: r.situacao === 'nao_encontrado' ? 'naoEncontrado' : 'falha' }));
    const d = r.dados;
    f.mesclar(
      campo === 'documento'
        ? { razao_social: d.razao_social, nome_fantasia: d.nome_fantasia, ...enderecoDaConsulta(d.endereco as Record<string, unknown>, true) }
        : enderecoDaConsulta(d, false),
    );
    setConsulta((c) => ({ ...c, [campo]: 'preenchido' }));
  }

  function mudar(nome: string, v: unknown) {
    // Lote 19/10: trocar o país troca a moeda para a dele e limpa o documento e o endereço do país anterior.
    if (nome === 'pais') {
      const p = String(v || 'BR');
      for (const [k, x] of Object.entries({ pais: p, moeda: moedaDoPais(p), documento: '', documento_tipo: '', cep: '', uf: '' })) f.definir(k, x);
      ultima.current = {};
      setConsulta({});
      return;
    }
    const pais = paisDe(f.valores);
    f.definir(nome, nome === 'cep' && pais !== 'BR' && typeof v === 'string' ? mascararCodigoPostal(pais, v) : v);
    // A consulta (BrasilAPI) é da Receita e dos Correios: só no Brasil.
    if (pais !== 'BR') return;
    const def = defs.find((d) => d.nome === nome && (!d.visivel || d.visivel(f.valores)));
    if (!def || (nome !== 'documento' && nome !== 'cep') || !s.pode('cadastros.ver')) return;
    const pronto = prontoParaConsulta(def, v);
    if (!pronto || ultima.current[nome] === pronto) return;
    ultima.current[nome] = pronto;
    void consultar(nome, pronto);
  }

  return (
    <form
      id="form-empresa"
      noValidate
      className={GRADE_FORM}
      onSubmit={(ev) =>
        f.enviar(ev, defs, tn('comum.obrigatorio'), async (v) => {
          const pais = paisDe(v);
          const corpo = {
            razao_social: v.razao_social,
            nome_fantasia: v.nome_fantasia || null,
            pais,
            moeda: v.moeda || null,
            documento_tipo: tipoDoDocumento(pais, v.documento_tipo, v.documento),
            documento: v.documento || null,
            endereco: { cep: v.cep, logradouro: v.logradouro, numero: v.numero, complemento: v.complemento, bairro: v.bairro, cidade: v.cidade, uf: v.uf },
          };
          if (empresa) {
            await patch(`/configuracoes/empresas/${empresa.id}`, corpo);
            await aoSalvar(empresa.id);
          } else {
            const r = await post<{ id: string }>('/configuracoes/empresas', corpo);
            await aoSalvar(r.id);
          }
        })
      }
    >
      <CamposForm defs={defs} valores={f.valores} erros={f.erros} erroGeral={f.erroGeral} aoMudar={mudar} />
    </form>
  );
}

/** M13.1: empresas do grupo (CRUD da tabela `empresas` do núcleo). */
/**
 * Review r41 visual (M1): cadastrar e arquivar empresa do grupo exigem ver todas as empresas (D19); quem não vê não tem os
 * botões "Nova empresa" e "Arquivar" (antes apareciam e sempre recusavam). Editar a empresa que vê continua.
 */
// Val5 (29/10): as empresas do grupo têm ação própria (`configuracoes.empresas`).
export const podeMudarGrupo = (s: { pode(permissao: string): boolean; empresas?: DadosSessao['empresas'] }) => s.pode('configuracoes.empresas') && veTodasAsEmpresas(s);

export function Empresas() {
  const { t } = useTranslation('configuracoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [aberto, setAberto] = useState<string | 'nova' | null>(null);
  const [arquivando, setArquivando] = useState(false);
  const q = useQuery({ queryKey: ['configuracoes', 'empresas'], queryFn: () => get<{ itens: Empresa[] }>('/configuracoes/empresas', { limite: 50 }) });
  const atual = aberto && aberto !== 'nova' ? (q.data?.itens.find((e) => e.id === aberto) ?? null) : null;
  const podeEditar = s.pode('configuracoes.empresas');
  const mudaGrupo = podeMudarGrupo(s);

  const recarregar = () => Promise.all([qc.invalidateQueries({ queryKey: ['configuracoes', 'empresas'] }), qc.invalidateQueries({ queryKey: ['configuracoes', 'historico'] }), qc.invalidateQueries({ queryKey: ['chatter'] })]);
  const arquivar = useMutation({
    // Item 5 (21/10): excluir é arquivar, com o nome digitado e, se escolhido, as verticais passadas a outra empresa.
    mutationFn: (p: { id: string; confirmacao: string; destinoId: string | null }) => post(`/configuracoes/empresas/${p.id}/excluir`, { confirmacao: p.confirmacao, destino_id: p.destinoId }),
    onSuccess: async () => {
      setArquivando(false);
      setAberto(null);
      await recarregar();
      toast.sucesso(tn('exclusao.estrutura.feito'));
    },
    onError: (e) => {
      setArquivando(false);
      toast.erro(mensagemDeErro(e));
    },
  });

  return (
    <>
      {mudaGrupo && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('nova')}>
            {t('acoes.novaEmpresa')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Cartao titulo={t('empresa.titulo')} semPadding>
        <Estado consulta={q}>
          {(d) =>
            d.itens.length === 0 ? (
              <Vazio texto={t('empresa.vazio')} icone={<Building2 className="size-5" aria-hidden />} />
            ) : (
              <ul className="divide-y divide-borda">
              {d.itens.map((e) => (
                <li key={e.id}>
                  <button type="button" onClick={() => setAberto(e.id)} className="flex w-full min-w-0 items-center gap-3 px-4 py-3 text-left hover:bg-sup-2">
                    <span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-borda bg-sup-2 text-tinta-3">
                      {e.logo_anexo_id ? <img src={urlLogo(e.logo_anexo_id)} alt="" className="size-full object-contain" /> : <Building2 className="size-5" aria-hidden />}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <TextoCortado className="font-medium">{e.nome_fantasia || e.razao_social}</TextoCortado>
                      <span className="truncate text-xs text-tinta-3" title={e.nome_fantasia ? e.razao_social : undefined}>{e.nome_fantasia ? e.razao_social : ''}</span>
                    </span>
                    <span className="flex shrink-0 flex-col items-end text-xs text-tinta-3">
                      <span className="numero">{documentoDoPais(e.pais, e.documento_tipo, e.documento)}</span>
                      <span>{[e.endereco.cidade, e.pais === 'ES' ? nomeProvinciaEs(e.endereco.uf) : e.endereco.uf].filter(Boolean).join(' / ')}</span>
                    </span>
                  </button>
                </li>
              ))}
              </ul>
            )
          }
        </Estado>
      </Cartao>

      <Drawer
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        largura="lg"
        titulo={atual ? atual.nome_fantasia || atual.razao_social : t('acoes.novaEmpresa')}
        rodape={
          podeEditar && (
            <>
              {atual && mudaGrupo && (
                <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setArquivando(true)}>
                  {tn('exclusao.estrutura.excluir')}
                </Botao>
              )}
              <>
                <Botao variante="fantasma" onClick={() => setAberto(null)}>
                  {tn('comum.cancelar')}
                </Botao>
                <Botao variante="primario" type="submit" form="form-empresa">
                  {tn('comum.salvar')}
                </Botao>
              </>
            </>
          )
        }
      >
        <div className="flex flex-col gap-5">
          {atual && <BlocoLogo empresa={atual} aoMudar={recarregar} />}
          <FormEmpresa
            key={atual?.id ?? 'nova'}
            empresa={atual}
            aoSalvar={async (id) => {
              await recarregar();
              toast.sucesso(tn('comum.salvo'));
              // Empresa nova continua aberta para enviar a logo; edição fecha.
              setAberto(atual ? null : id);
            }}
          />
          {atual && (
            <div className="border-t border-borda pt-4">
              <Chatter entidade="configuracoes.empresa" registroId={atual.id} titulo={atual.nome_fantasia || atual.razao_social} />
            </div>
          )}
        </div>
      </Drawer>

      <ConfirmarExclusao
        aberto={arquivando}
        aoFechar={() => setArquivando(false)}
        aoConfirmar={(p) => atual && arquivar.mutate({ id: atual.id, ...p })}
        carregando={arquivar.isPending}
        titulo={t('empresa.confirmarArquivar')}
        texto={t('empresa.confirmarArquivarTexto')}
        nome={atual?.nome_fantasia || atual?.razao_social || ''}
        outrosNomes={[atual?.razao_social]}
        destinos={(q.data?.itens ?? []).filter((e) => e.id !== atual?.id).map((e) => ({ valor: e.id, rotulo: e.nome_fantasia || e.razao_social }))}
      />
    </>
  );
}
